import { Component, Input, Output, EventEmitter, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ReferralLayer, StatusGroup, SubStatusGroup, StatusReason, PixelData } from '../charts/pixel-map/pixel-map.component';

// Define a type for our new flat list items for clarity
export type FlatHierarchyItem = (ReferralLayer | StatusGroup | SubStatusGroup | StatusReason) & {
  type: 'referral' | 'group' | 'subgroup' | 'reason';
  level: number;
  parentReferral?: string; // Added for unique identification
  parentGroup?: string;    // Added for unique identification

  // TypeScript 5.8 distributes this intersection across the union, so a property
  // declared by only some members is no longer reachable from the template. These
  // are read on every row regardless of which variant it is, so they are declared
  // here as optional rather than casting at each of the dozen template sites.
  name?: string;
  code?: string;
  desc?: string;
  isExpanded?: boolean;
  indeterminate?: boolean;
};

@Component({
    selector: 'app-status-selector',
    templateUrl: './status-selector.component.html',
    styleUrls: ['./status-selector.component.scss'],
    standalone: false
})
export class StatusSelectorComponent implements OnInit, AfterViewInit {
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
  @Input() hierarchy: ReferralLayer[] = [];
  @Input() showCheckboxes = true;
  @Input() showColorPickers = true;
  @Input() allowColorChange = true; // NEW: Controls if colors are editable
  @Input() focusOn: PixelData | null = null;
  @Input() initialSelection: StatusReason[] | null = null;
  // This will emit the updated hierarchy to the parent dialog component
  @Output() hierarchyChange = new EventEmitter<ReferralLayer[]>();

  localHierarchy: ReferralLayer[] = [];
  filteredHierarchy: ReferralLayer[] = [];
  // This will be the data source for the virtual scroller
  public flatHierarchy: FlatHierarchyItem[] = []; searchText = '';
  allExpanded = false;

  @Input() startClean = false; // NEW: If true, all checkboxes will start unchecked (clean slate)

  ngOnInit(): void {
    // Create a local copy to work with
    this.localHierarchy = JSON.parse(JSON.stringify(this.hierarchy));

    // --- NEW: Clean Slate Logic ---
    if (this.startClean) {
      this.clearAllSelections(this.localHierarchy);
    }

    if (this.focusOn) {
      const focusData = this.focusOn;
      const referralLayer = this.localHierarchy.find(r => r.name === focusData.CURRENT_REFERRAL_STATUS);

      if (referralLayer) {
        referralLayer.isExpanded = true;
        const parentGroup = referralLayer.groups.find(g => g.name === focusData.STATUS_GROUP);

        if (parentGroup) {
          parentGroup.isExpanded = true;
          const subGroup = parentGroup.subGroups.find(sg => sg.name === focusData.STATUS_SUB_GROUP);

          if (subGroup) {
            subGroup.isExpanded = true;
            const reason = subGroup.reasons.find(r => r.code === focusData.STATUS_REASON_CODE);

            if (reason) {
              reason.checked = true;
              // Update chain: Reason -> SubGroup -> Group -> Referral
              this.updateAllParents(referralLayer, parentGroup, subGroup);
            }
          }
        }
      }
    }
    this.filteredHierarchy = this.localHierarchy;

    // --- NEW: Apply Initial Selection (from Query Builder) ---
    if (this.initialSelection && Array.isArray(this.initialSelection) && this.initialSelection.length > 0) {
      const selectedCodes = new Set(this.initialSelection.map(s => s.code));
      
      this.localHierarchy.forEach(referral => {
        referral.groups.forEach(group => {
          group.subGroups.forEach(subGroup => {
            subGroup.reasons.forEach(reason => {
              if (selectedCodes.has(reason.code)) {
                reason.checked = true;
              }
            });
          });
        });
      });
    }
    
    // --- FIX: Recalculate parent states to ensure UI sync ---
    this.recalculateAllParentStates();

    // Expand all by default so the user sees all codes
    // this.toggleAllPanels(true);
    this.buildFlatHierarchy();
  }

  /**
   * Public method to clear all selections.
   * Can be called by parent components (e.g. Manage Statuses Dialog -> Clear All)
   */
  public clearSelections(): void {
    this.clearAllSelections(this.localHierarchy);
    this.recalculateAllParentStates();
    this.buildFlatHierarchy();
    this.hierarchyChange.emit(this.localHierarchy);
  }

  /**
   * Helper to Recursively clear all checked/indeterminate states
   */
  private clearAllSelections(hierarchy: ReferralLayer[]): void {
    hierarchy.forEach(referral => {
      referral.checked = false;
      referral.indeterminate = false;
      referral.groups.forEach(group => {
        group.checked = false;
        group.indeterminate = false;
        group.subGroups.forEach(subGroup => {
          subGroup.checked = false;
          subGroup.indeterminate = false;
          subGroup.reasons.forEach(reason => {
            reason.checked = false;
          });
        });
      });
    });
  }

  ngAfterViewInit(): void {
    // --- SCROLL LOGIC ---
    // We do this in AfterViewInit to ensure the DOM elements exist
    if (this.focusOn) {
      const focusData = this.focusOn
      // Use a short timeout to allow the DOM to update after expansion
      setTimeout(() => {
        const elementId = `status-reason-${focusData.STATUS_REASON_CODE}`;
        const element = document.getElementById(elementId);
        if (element) {
          // Scroll the element into the middle of the view for better visibility
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }

  onSearchChange(): void {
    const searchText = this.searchText.trim().toLowerCase();

    if (!searchText) {
      this.filteredHierarchy = this.localHierarchy;
    } else {
      // --- UPDATED: Filter logic for 4 levels ---
      this.filteredHierarchy = this.localHierarchy.map(referral => {
        const filteredReferral = { ...referral };

        const filteredGroups = referral.groups.map(group => {
          const filteredGroup = { ...group };

          const filteredSubGroups = group.subGroups.map(subGroup => {
            const filteredSubGroup = { ...subGroup };

            const filteredReasons = subGroup.reasons.filter(reason =>
              reason.desc.toLowerCase().includes(searchText) ||
              reason.code.toLowerCase().includes(searchText)
            );
            filteredSubGroup.reasons = filteredReasons;
            return filteredSubGroup;
          }).filter(subGroup =>
            subGroup.name.toLowerCase().includes(searchText) || subGroup.reasons.length > 0
          );

          filteredGroup.subGroups = filteredSubGroups;
          return filteredGroup;
        }).filter(group =>
          group.name.toLowerCase().includes(searchText) || group.subGroups.length > 0
        );

        filteredReferral.groups = filteredGroups;
        return filteredReferral;
      }).filter(referral =>
        referral.name.toLowerCase().includes(searchText) || referral.groups.length > 0
      );
    }

    if (this.searchText) {
      this.toggleAllPanels(true);
    }
    this.buildFlatHierarchy();
  }

  /// For handling a single reason click
  public handleReasonSelectionChange(changedReason: StatusReason): void {
    // 1. Loop through Level 0 (Referral Layers)
    for (const referral of this.localHierarchy) {
      // 2. Loop through Level 1 (Status Groups)
      for (const group of referral.groups) {
        // 3. Loop through Level 2 (Sub-Groups)
        for (const subGroup of group.subGroups) {
          // 4. Check if the changed reason belongs to this Sub-Group
          if (subGroup.reasons.some(reason => reason.code === changedReason.code)) {

            // Found the path! Update the chain: Level 3 -> 2 -> 1 -> 0
            this.updateAllParents(referral, group, subGroup);

            return; // Exit after processing the match
          }
        }
      }
    }
  }

  // Level 3 -> Level 2 -> Level 1
  updateAllParents(referral: ReferralLayer, group: StatusGroup, subGroup: SubStatusGroup): void {
    this.updateSubGroupCheckbox(subGroup);
    this.updateParentCheckbox(group);
    this.updateReferralCheckbox(referral);
    this.hierarchyChange.emit(this.localHierarchy);
  }

  /**
   * Recalculates the checked/indeterminate state of all parent groups and referrals
   * based on the state of their children (SubGroups/Reasons).
   * This is crucial on init to ensure data integrity if the input hierarchy is partially checked.
   */
  recalculateAllParentStates(): void {
    // Traverse bottom-up conceptually, but we iterate top-down and update bottom-up helpers.
    this.localHierarchy.forEach(referral => {
      referral.groups.forEach(group => {
        group.subGroups.forEach(subGroup => {
          // 1. Sync SubGroup state from Reasons
          this.updateSubGroupCheckbox(subGroup);
        });
        // 2. Sync Group state from SubGroups
        this.updateParentCheckbox(group);
      });
      // 3. Sync Referral state from Groups
      this.updateReferralCheckbox(referral);
    });
  }

  updateReferralCheckbox(referral: ReferralLayer): void {
    const allChildrenChecked = referral.groups.every(g => g.checked && !g.indeterminate);
    const noChildrenChecked = referral.groups.every(g => !g.checked && !g.indeterminate);

    if (allChildrenChecked) {
      referral.checked = true;
      referral.indeterminate = false;
    } else if (noChildrenChecked) {
      referral.checked = false;
      referral.indeterminate = false;
    } else {
      referral.checked = false;
      referral.indeterminate = true;
    }
  }

  // Level 2 (Group) Logic
  updateParentCheckbox(group: StatusGroup): void {
    const allChildrenChecked = group.subGroups.every(sg => sg.checked && !sg.indeterminate);
    const noChildrenChecked = group.subGroups.every(sg => !sg.checked && !sg.indeterminate);

    if (allChildrenChecked) {
      group.checked = true;
      group.indeterminate = false;
    } else if (noChildrenChecked) {
      group.checked = false;
      group.indeterminate = false;
    } else {
      group.checked = false;
      group.indeterminate = true;
    }
  }

  // Level 3 (SubGroup) Logic
  updateSubGroupCheckbox(subGroup: SubStatusGroup): void {
    const allChecked = subGroup.reasons.every(r => r.checked);
    const noneChecked = subGroup.reasons.every(r => !r.checked);
    subGroup.checked = allChecked;
    subGroup.indeterminate = !allChecked && !noneChecked;
  }

  public onReasonRowClick(reason: StatusReason): void {
    reason.checked = !reason.checked;

    // Find parents
    for (const referral of this.localHierarchy) {
      for (const group of referral.groups) {
        for (const subGroup of group.subGroups) {
          if (subGroup.reasons.some(r => r.code === reason.code)) {
            this.updateAllParents(referral, group, subGroup);
            return;
          }
        }
      }
    }
  }

  // expand all panels
  toggleAllPanels(setExpanded?: boolean): void {
    if (setExpanded != undefined) {
      this.allExpanded = setExpanded;
    } else {
      this.allExpanded = !this.allExpanded;
    }

    this.filteredHierarchy.forEach(referral => {
      referral.isExpanded = this.allExpanded;
      referral.groups.forEach(group => {
        group.isExpanded = this.allExpanded;
        group.subGroups.forEach(subGroup => {
          subGroup.isExpanded = this.allExpanded;
        });
      });
    });
    this.buildFlatHierarchy();
  }

  /**
   * Calculates what to display in the main collapsed summary line.
   * It shows a count of the currently checked parent groups.
   */
  getSummaryLabel(): string {
    const checkedCount = this.localHierarchy.filter(g => g.checked).length;
    if (checkedCount === this.localHierarchy.length) {
      return 'All Statuses';
    }
    if (checkedCount === 0) {
      return 'No Statuses Selected';
    }
    return `${checkedCount} of ${this.localHierarchy.length} Status Groups Selected`;
  }

  /**
   * Finds the parent StatusGroup for a given SubStatusGroup.
   * Iterates through ReferralLayer -> StatusGroups to find the match.
   * @param subGroup The SubStatusGroup whose parent we need to find.
   * @returns The parent StatusGroup or null if not found.
   */
  public findParentGroup(subGroup: SubStatusGroup): StatusGroup | null {
    // 1. Loop through the top level (Referral Layers)
    for (const referral of this.localHierarchy) {
      // 2. Loop through the Status Groups
      for (const group of referral.groups) {
        // 3. Check if this group contains the subGroup we are looking for
        if (group.subGroups.some(sg => sg.name === subGroup.name)) {
          return group;
        }
      }
    }
    console.error('Could not find parent for sub-group:', subGroup);
    return null;
  }


  /**
   * Toggles the expansion state of a group or sub-group and rebuilds the flat list.
   * This version correctly finds the original object in the hierarchy to modify its state.
   */
  toggleExpansion(item: FlatHierarchyItem): void {
    let originalItem: ReferralLayer | StatusGroup | SubStatusGroup | undefined;

    if (item.type === 'referral') {
      originalItem = this.localHierarchy.find(r => r.name === (item as ReferralLayer).name);
    }
    else if (item.type === 'group') {
      // Must find parent Referral first
      const groupItem = item as StatusGroup;
      // Use parentReferral if available (it should be now)
      if (item.parentReferral) {
        const referral = this.localHierarchy.find(r => r.name === item.parentReferral);
        if (referral) {
          originalItem = referral.groups.find(g => g.name === groupItem.name);
        }
      } else {
        // Fallback to old logic (shouldn't happen if buildFlatHierarchy is correct)
        for (const referral of this.localHierarchy) {
          originalItem = referral.groups.find(g => g.name === groupItem.name);
          if (originalItem) break;
        }
      }
    }
    else if (item.type === 'subgroup') {
      // Must find parent Group first
      const subGroupItem = item as SubStatusGroup;
      if (item.parentReferral && item.parentGroup) {
        const referral = this.localHierarchy.find(r => r.name === item.parentReferral);
        if (referral) {
          const group = referral.groups.find(g => g.name === item.parentGroup);
          if (group) {
            originalItem = group.subGroups.find(sg => sg.name === subGroupItem.name);
          }
        }
      } else {
        // Fallback
        for (const referral of this.localHierarchy) {
          for (const group of referral.groups) {
            originalItem = group.subGroups.find(sg => sg.name === subGroupItem.name);
            if (originalItem) break;
          }
          if (originalItem) break;
        }
      }
    }

    if (originalItem) {
      originalItem.isExpanded = !originalItem.isExpanded;
      this.buildFlatHierarchy();
    }
  }



  /**
   * Creates a flat array from the nested filteredHierarchy.
   * This function is the core of the virtual scrolling solution.
   */
  buildFlatHierarchy(): void {
    this.flatHierarchy = [];
    this.filteredHierarchy.forEach(referral => {
      // Level 0: Referral
      this.flatHierarchy.push({ ...referral, type: 'referral', level: 0 });

      if (referral.isExpanded) {
        referral.groups.forEach(group => {
          // Level 1: Group
          this.flatHierarchy.push({
            ...group,
            type: 'group',
            level: 1,
            parentReferral: referral.name // Store parent referral name
          });

          if (group.isExpanded) {
            group.subGroups.forEach(subGroup => {
              // Level 2: SubGroup
              this.flatHierarchy.push({
                ...subGroup,
                type: 'subgroup',
                level: 2,
                parentReferral: referral.name, // Store parent referral name
                parentGroup: group.name        // Store parent group name
              });

              if (subGroup.isExpanded) {
                subGroup.reasons.forEach(reason => {
                  // Level 3: Reason
                  this.flatHierarchy.push({ 
                    ...reason, 
                    type: 'reason', 
                    level: 3,
                    parentReferral: referral.name,
                    parentGroup: group.name 
                  });
                });
              }
            });
          }
        });
      }
    });

  }

  /**
   * Reruns the filter if searching, or rebuilds the flat list if not.
   * This ensures the UI reflects the changes made to the master localHierarchy.
   */
  refreshView(): void {
    if (this.searchText && this.searchText.trim() !== '') {
      // If searching, we must re-run the search logic to map 
      // the updated 'checked' states from localHierarchy to a new filteredHierarchy
      this.onSearchChange();
    } else {
      // If not searching, just flatten the updated localHierarchy
      this.filteredHierarchy = this.localHierarchy;
      this.buildFlatHierarchy();
    }
  }

  // A new trackBy function for the flat list
  trackByItem(index: number, item: FlatHierarchyItem): string {
    return item.type + ('code' in item ? item.code : item.name);
  }

  // Level 0 Clicked (Referral)
  toggleAllReferral(referral: ReferralLayer): void {
    referral.indeterminate = false;
    referral.groups.forEach(g => {
      g.checked = referral.checked;
      g.indeterminate = false;
      g.subGroups.forEach(sg => {
        sg.checked = referral.checked;
        sg.indeterminate = false;
        sg.reasons.forEach(r => r.checked = referral.checked);
      });
    });
    this.hierarchyChange.emit(this.localHierarchy);
  }

  // Parent status was clicked so make the parent, all sub status, and all reason codes checked to true
  // Level 1 -> Level 2 -> Level 3
  toggleAllSubGroups(referral: ReferralLayer, group: StatusGroup): void {
    group.indeterminate = false;
    group.subGroups.forEach(sg => {
      sg.checked = group.checked;
      sg.indeterminate = false;
      sg.reasons.forEach(r => r.checked = group.checked);
    });
    this.updateReferralCheckbox(referral); // Update parent
    this.hierarchyChange.emit(this.localHierarchy);
  }

  // Sub status was clicked so make all reason codes in the sub status checked to true
  // Level 2 Clicked (SubGroup)
  toggleAllReasons(referral: ReferralLayer, group: StatusGroup, subGroup: SubStatusGroup): void {
    subGroup.indeterminate = false;
    subGroup.reasons.forEach(r => r.checked = subGroup.checked);
    this.updateParentCheckbox(group); // Update parent
    this.updateReferralCheckbox(referral); // Update grandparent
    this.hierarchyChange.emit(this.localHierarchy);
  }

  /**
   * Level 0: Referral Checkbox
   */
  onReferralCheckboxChange(flatItem: ReferralLayer, isChecked: boolean): void {
    // 1. Find original
    const original = this.localHierarchy.find(r => r.name === flatItem.name);
    if (original) {
      // 2. Sync state
      original.checked = isChecked;
      // 3. Run Logic on ORIGINAL
      this.toggleAllReferral(original);
      // 4. Refresh View
      this.refreshView();
    }
  }

  /**
   * Level 1: Group Checkbox
   */
  onGroupCheckboxChange(flatItem: StatusGroup, isChecked: boolean): void {
    // 1. Find original parent and group
    let referral: ReferralLayer | undefined;
    let originalGroup: StatusGroup | undefined;

    // Use parentReferral if available (from FlatHierarchyItem)
    const flatGroup = flatItem as any; // Type access to extra props
    if (flatGroup.parentReferral) {
      referral = this.localHierarchy.find(r => r.name === flatGroup.parentReferral);
      if (referral) {
        originalGroup = referral.groups.find(g => g.name === flatItem.name);
      }
    } else {
      // Fallback search
      for (const r of this.localHierarchy) {
        const g = r.groups.find(grp => grp.name === flatItem.name);
        if (g) {
          referral = r;
          originalGroup = g;
          break;
        }
      }
    }

    if (referral && originalGroup) {
      // 2. Sync state
      originalGroup.checked = isChecked;
      // 3. Run Logic
      this.toggleAllSubGroups(referral, originalGroup);
      // 4. Refresh View
      this.refreshView();
    }
  }

  /**
   * Level 2: SubGroup Checkbox
   */
  onSubGroupCheckboxChange(flatItem: SubStatusGroup, isChecked: boolean): void {
    let referral: ReferralLayer | undefined;
    let group: StatusGroup | undefined;
    let originalSub: SubStatusGroup | undefined;

    const flatSub = flatItem as any;
    if (flatSub.parentReferral && flatSub.parentGroup) {
      referral = this.localHierarchy.find(r => r.name === flatSub.parentReferral);
      if (referral) {
        group = referral.groups.find(g => g.name === flatSub.parentGroup);
        if (group) {
          originalSub = group.subGroups.find(sg => sg.name === flatItem.name);
        }
      }
    } else {
      // Fallback manual search
      for (const r of this.localHierarchy) {
        for (const g of r.groups) {
          const sub = g.subGroups.find(sg => sg.name === flatItem.name);
          if (sub) {
            referral = r;
            group = g;
            originalSub = sub;
            break;
          }
        }
        if (originalSub) break;
      }
    }

    if (referral && group && originalSub) {
      // Sync
      originalSub.checked = isChecked;
      // Logic
      this.toggleAllReasons(referral, group, originalSub);
      // Refresh
      this.refreshView();
    }
  }

  /**
   * Level 3: Reason Checkbox
   */
  onReasonCheckboxChange(flatItem: StatusReason, isChecked: boolean): void {
    let referral: ReferralLayer | undefined;
    let group: StatusGroup | undefined;
    let subGroup: SubStatusGroup | undefined;
    let originalReason: StatusReason | undefined;

    const flatReason = flatItem as any;
    // Note: We need parentSubGroup logic if we want to be fully precise down to subgroup, 
    // but typically finding the subGroup in the specific parentGroup is enough unless subGroups mimic names too.
    // However, our data structure doesn't easily give us subGroup name from flatItem properties unless we added it.
    // Wait, we didn't add parentSubGroup to FlatHierarchyItem.
    // Let's rely on finding the reason code within the target parentGroup's subgroups.
    // Or better, let's just stick to the search within the identified Referral->Group.
    
    if (flatReason.parentReferral && flatReason.parentGroup) {
      referral = this.localHierarchy.find(r => r.name === flatReason.parentReferral);
      if (referral) {
        group = referral.groups.find(g => g.name === flatReason.parentGroup);
        if (group) {
           // We need to find which subgroup this reason belongs to.
           for (const sg of group.subGroups) {
             const r = sg.reasons.find(reason => reason.code === flatItem.code);
             if (r) {
               subGroup = sg;
               originalReason = r;
               break;
             }
           }
        }
      }
    } else {
       // Fallback global search
       for (const r of this.localHierarchy) {
         for (const g of r.groups) {
           for (const sg of g.subGroups) {
             const reason = sg.reasons.find(re => re.code === flatItem.code);
             if (reason) {
               referral = r;
               group = g;
               subGroup = sg;
               originalReason = reason;
               break;
             }
           }
           if (originalReason) break;
         }
         if (originalReason) break;
       }
    }

    if (referral && group && subGroup && originalReason) {
       // Sync
       originalReason.checked = isChecked;
       // Logic: Skip handleReasonSelectionChange's search and call updateAllParents directly
       this.updateAllParents(referral, group, subGroup);
       // Refresh
       this.refreshView();
    }
  }


  /**
 * Finds the parent ReferralLayer for a given StatusGroup.
 */
  public findParentReferral(group: StatusGroup): ReferralLayer | null {
    return this.localHierarchy.find(referral =>
      referral.groups.some(g => g.name === group.name)
    ) || null;
  }

  /**
   * Finds the grandparent ReferralLayer for a given SubStatusGroup.
   */
  public findGrandparentReferral(subGroup: SubStatusGroup): ReferralLayer | null {
    for (const referral of this.localHierarchy) {
      for (const group of referral.groups) {
        if (group.subGroups.some(sg => sg.name === subGroup.name)) {
          return referral;
        }
      }
    }
    return null;
  }

  /**
 * Traverses the local hierarchy and returns a flat array of all StatusReason items that are checked.
 * This is called by the parent component to get the final selection.
 */
  getCheckedReasons(): StatusReason[] {
    const checkedReasons: StatusReason[] = [];
    this.localHierarchy.forEach(referral => {
      if (referral.checked || referral.indeterminate) {
        referral.groups.forEach(group => {
          if (group.checked || group.indeterminate) {
            group.subGroups.forEach(subGroup => {
              if (subGroup.checked || subGroup.indeterminate) {
                subGroup.reasons.forEach(reason => {
                  if (reason.checked) checkedReasons.push(reason);
                });
              }
            });
          }
        });
      }
    });
    return checkedReasons;
  }

  // --- Color Logic ---
  /**
   * 1. Handle Referral (Level 0) Color Change
   */
  onReferralColorChange(flatItem: ReferralLayer, newColor: string): void {
    // 1. Update the visual copy immediately so the UI doesn't lag
    flatItem.color = newColor;

    // 2. Find the original in localHierarchy
    const original = this.localHierarchy.find(r => r.name === flatItem.name);

    if (original) {
      original.color = newColor;
      original.groups.forEach(g => {
        g.color = newColor;
        g.subGroups.forEach(sg => {
          sg.color = newColor;
          sg.reasons.forEach(r => r.color = newColor);
        });
      });

      this.hierarchyChange.emit(this.localHierarchy);

      this.buildFlatHierarchy();
    }
  }


  /**
   * 2. Handle Group (Level 1) Color Change
   */
  onParentColorChange(flatItem: StatusGroup, newColor: string): void {
    flatItem.color = newColor;

    // Find original
    for (const referral of this.localHierarchy) {
      const originalGroup = referral.groups.find(g => g.name === flatItem.name);
      if (originalGroup) {
        originalGroup.color = newColor;

        // Cascade color change to children (SubGroups and Reasons)
        originalGroup.subGroups.forEach(sg => {
          sg.color = newColor;
          sg.reasons.forEach(r => r.color = newColor);
        });

        this.hierarchyChange.emit(this.localHierarchy);
        this.buildFlatHierarchy();
        return;
      }
    }
  }

  /**
   * 3. Handle SubGroup (Level 2) Color Change
   */
  onSubGroupColorChange(parentGroup: StatusGroup, flatItem: SubStatusGroup, newColor: string): void {
    flatItem.color = newColor;

    // Find original
    for (const referral of this.localHierarchy) {
      const originalGroup = referral.groups.find(g => g.name === parentGroup.name);
      if (originalGroup) {
        const originalSub = originalGroup.subGroups.find(sg => sg.name === flatItem.name);
        if (originalSub) {
          originalSub.color = newColor;

          // Cascade to reasons
          originalSub.reasons.forEach(r => r.color = newColor);

          this.hierarchyChange.emit(this.localHierarchy);
          this.buildFlatHierarchy();
          return;
        }
      }
    }
  }

  /**
   * 4. Handle Reason (Level 3) Color Change
   */
  onReasonColorChange(flatItem: StatusReason, newColor: string): void {
    flatItem.color = newColor;

    // Find original (Traverse 4 layers)
    for (const referral of this.localHierarchy) {
      for (const group of referral.groups) {
        for (const subGroup of group.subGroups) {
          const originalReason = subGroup.reasons.find(r => r.code === flatItem.code);
          if (originalReason) {
            originalReason.color = newColor;
            this.hierarchyChange.emit(this.localHierarchy);
            return;
          }
        }
      }
    }
  }

  trackByGroup(index: number, group: StatusGroup): string {
    return group.name;
  }

  trackBySubGroup(index: number, subGroup: SubStatusGroup): string {
    return subGroup.name;
  }

  trackByReason(index: number, reason: StatusReason): string {
    return reason.code;
  }

  // HELPER METHODS FOR TEMPLATE TYPE CASTING
  // These allow the HTML template to treat the generic 'item' as a specific type

  asReferral(item: FlatHierarchyItem): ReferralLayer {
    return item as unknown as ReferralLayer;
  }

  asGroup(item: FlatHierarchyItem): StatusGroup {
    return item as unknown as StatusGroup;
  }

  asSubGroup(item: FlatHierarchyItem): SubStatusGroup {
    return item as unknown as SubStatusGroup;
  }

  asReason(item: FlatHierarchyItem): StatusReason {
    return item as unknown as StatusReason;
  }

  // --- Viewport / Positioning Logic ---

  getPickerPosition(index: number): string {
    if (!this.viewport) return 'auto';

    const range = this.viewport.getRenderedRange();
    // If the item is among the last visible items (accounting for buffer), force top.
    // range.end includes buffer items (approx 2-4 items with 100/200px buffer).
    // range.end - 6 roughly targets the last 4 visible items plus buffer.
    const isNearBottom = (index >= range.end - 6) || (index >= this.flatHierarchy.length - 4);

    return isNearBottom ? 'top' : 'auto';
  }
}